import React, { useState } from 'react';
import { Monitor, ShoppingCart, Shield, Wifi, HardDrive, Wrench, Home, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import ServiceModal from '../components/ServiceModal';

const PrivateServicesPage = ({ language }) => {
  const [activeModal, setActiveModal] = useState(null);
  const translations = {
    de: {
      title: 'IT-Services für Privatkunden',
      subtitle: 'Zuverlässige Unterstützung für Ihren digitalen Alltag',
      services: [
        {
          icon: Monitor,
          title: 'Umstieg auf Windows 11',
          description: 'Installation, Datenübertragung und Einrichtung – damit Sie sofort produktiv arbeiten können.'
        },
        {
          icon: ShoppingCart,
          title: 'Kaufberatung & Einrichtung',
          description: 'Neue PCs, Laptops oder Drucker – ich berate Sie beim Kauf und richte alles perfekt ein.'
        },
        {
          icon: Shield,
          title: 'Schutz vor Viren & Betrug',
          description: 'Passende Sicherheitslösungen für Ihren Alltag – schützen Sie sich effektiv vor digitalen Gefahren.'
        },
        {
          icon: Wifi,
          title: 'Router- & WLAN-Einrichtung',
          description: 'Stabiles Internet im ganzen Haus – optimale WLAN-Abdeckung und sichere Konfiguration.'
        },
        {
          icon: HardDrive,
          title: 'Datensicherung & Wiederherstellung',
          description: 'Ihre Daten bleiben sicher und verfügbar – automatische Backups und schnelle Datenrettung.'
        },
        {
          icon: Wrench,
          title: 'Hilfe bei Softwareproblemen',
          description: 'Installation, Updates und Fehlersuche – ich löse Ihre Software-Herausforderungen.'
        },
        {
          icon: Home,
          title: 'Smart-Home-Unterstützung',
          description: 'Technik, die mitdenkt und zuverlässig funktioniert – von der Planung bis zur Einrichtung.'
        }
      ],
      cta: {
        title: 'Brauchen Sie Hilfe?',
        description: 'Kontaktieren Sie mich für eine persönliche Beratung',
        button: 'Jetzt Kontakt aufnehmen'
      }
    },
    en: {
      title: 'IT Services for Private Customers',
      subtitle: 'Reliable support for your digital everyday life',
      services: [
        {
          icon: Monitor,
          title: 'Windows 11 Migration',
          description: 'Installation, data transfer and setup – so you can be productive right away.'
        },
        {
          icon: ShoppingCart,
          title: 'Purchase Advice & Setup',
          description: 'New PCs, laptops or printers – I advise you on the purchase and set everything up perfectly.'
        },
        {
          icon: Shield,
          title: 'Protection Against Viruses & Fraud',
          description: 'Suitable security solutions for your everyday life – protect yourself effectively against digital threats.'
        },
        {
          icon: Wifi,
          title: 'Router & WiFi Setup',
          description: 'Stable internet throughout the house – optimal WiFi coverage and secure configuration.'
        },
        {
          icon: HardDrive,
          title: 'Data Backup & Recovery',
          description: 'Your data stays safe and available – automatic backups and fast data recovery.'
        },
        {
          icon: Wrench,
          title: 'Help with Software Problems',
          description: 'Installation, updates and troubleshooting – I solve your software challenges.'
        },
        {
          icon: Home,
          title: 'Smart Home Support',
          description: 'Technology that thinks along and works reliably – from planning to setup.'
        }
      ],
      cta: {
        title: 'Need Help?',
        description: 'Contact me for personal consultation',
        button: 'Contact Me Now'
      }
    }
  };

  const t = translations[language];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-4" style={{ color: '#E53D4A' }}>
              {t.title}
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              {t.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {t.services.map((service, index) => {
              const IconComponent = service.icon;
              // Map services to modal types - now all 7 services have modals
              const modalTypes = ['windows11', 'pcSetup', 'virusProtection', 'routerSetup', 'dataBackup', 'softwareHelp', 'smartHome'];
              const modalType = modalTypes[index];
              
              return (
                <Card 
                  key={index} 
                  className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                  onClick={() => setActiveModal(modalType)}
                >
                  <CardHeader>
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#E53D4A] flex items-center justify-center">
                        <IconComponent className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-xl mb-2 flex items-center justify-between" style={{ color: '#E53D4A' }}>
                          <span>{service.title}</span>
                          <span className="text-xs bg-[#E53D4A] text-white px-2 py-1 rounded-full">
                            {language === 'de' ? 'Details' : 'Details'}
                          </span>
                        </CardTitle>
                        <CardDescription className="text-base">
                          {service.description}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#E53D4A] text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-4">
              {t.cta.title}
            </h2>
            <p className="text-xl mb-8 text-gray-200">
              {t.cta.description}
            </p>
            <Button 
              size="lg" 
              className="bg-white text-[#E53D4A] hover:bg-gray-100"
              asChild
            >
              <a href="mailto:kontakt@maier.systems">
                {t.cta.button}
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Service Modals */}
      {activeModal && (
        <ServiceModal 
          isOpen={true}
          onClose={() => setActiveModal(null)}
          language={language}
          serviceType={activeModal}
        />
      )}
    </div>
  );
};

export default PrivateServicesPage;
